import { useAuth } from "@/_core/hooks/useAuth";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";
import { AlertCircle, ArrowLeft, Save } from "lucide-react";
import { useEffect, useMemo, useState, useRef, type FormEvent, type ReactNode } from "react";
import { useLocation, useRoute, useSearch } from "wouter";

import { isNewWorkSearch, newWorkCustomerPath, isDirectWorkSearch, directWorkCustomerPath } from "@/lib/newWorkFlow";

type EntityKind =
  | "company"
  | "public_administration"
  | "ute"
  | "association_foundation"
  | "self_employed"
  | "individual";

const ENTITY_KIND_OPTIONS: Array<{ value: EntityKind; label: string }> = [
  { value: "company", label: "Empresa" },
  { value: "public_administration", label: "Administración pública" },
  { value: "ute", label: "UTE" },
  { value: "association_foundation", label: "Asociación / Fundación" },
  { value: "self_employed", label: "Autónomo" },
  { value: "individual", label: "Particular" },
];

const PAYMENT_METHODS = [
  { value: "transfer", label: "Transferencia" },
  { value: "check", label: "Cheque" },
  { value: "direct_debit", label: "Domiciliación" },
  { value: "cash", label: "Efectivo" },
  { value: "other", label: "Otro" },
];

export const CUSTOMER_ACCESS_ROLES = ["admin", "admin_manager", "sales", "project_manager"];

export function canManageCustomers(
  role: string | null | undefined,
  canCreateCustomer: boolean | null | undefined,
): boolean {
  if (!role || !CUSTOMER_ACCESS_ROLES.includes(role)) return false;
  if (role === "sales") return true;
  return canCreateCustomer === true;
}

// Radix Select does not accept empty string values; use a sentinel so the
// component renders correctly and we map back to null on save.
const SALES_OWNER_UNASSIGNED = "__UNASSIGNED__";

function toSalesOwnerSelect(id: string | null | undefined): string {
  return id && id !== "" ? id : SALES_OWNER_UNASSIGNED;
}

function fromSalesOwnerSelect(v: string): number | null {
  return v === SALES_OWNER_UNASSIGNED || v === "" ? null : Number(v);
}

function partyTypeFor(kind: EntityKind): "organization" | "person" {
  return kind === "self_employed" || kind === "individual" ? "person" : "organization";
}

function normalizedTaxIdForDuplicateCheck(value: string): string | null {
  const normalized = value.trim().toUpperCase().replace(/[\s\-.]/g, "");
  return /^(?:\d{8}[A-Z]|[XYZ]\d{7}[A-Z]|[ABCDEFGHJKLNPQRSUVW]\d{7}[0-9A-Z])$/.test(normalized)
    ? normalized
    : null;
}

function legalNameFromParts(form: CustomerFormState): string {
  if (partyTypeFor(form.entityKind) === "person") {
    return [form.firstName.trim(), form.lastName.trim()].filter(Boolean).join(" ").trim();
  }
  return form.legalName.trim();
}

function cleanError(message: string): string {
  const map: Record<string, string> = {
    "Razón social obligatoria.": "Razón social obligatoria.",
    "Introduce un email válido.": "Introduce un email válido.",
    "El teléfono debe tener al menos 6 dígitos.": "El teléfono debe tener al menos 6 dígitos.",
    "El CIF/NIF/NIE no es válido.": "El CIF/NIF/NIE no es válido.",
    "Ya existe una entidad con ese documento fiscal.": "Ya existe una entidad con ese documento fiscal.",
    "La dirección fiscal es obligatoria para convertir a Cliente.": "La dirección fiscal es obligatoria para convertir a Cliente.",
    "El responsable comercial es obligatorio.": "El responsable comercial es obligatorio.",
    "Añade un contacto principal o una justificación.": "Añade un contacto principal o una justificación.",
    "Web no válida.": "Web no válida.",
    "Email general no válido.": "Email general no válido.",
    "Email de facturación no válido.": "Email de facturación no válido.",
    "Email de contacto principal no válido.": "Email de contacto principal no válido.",
    "El teléfono general debe tener al menos 6 dígitos.": "El teléfono general debe tener al menos 6 dígitos.",
    "Teléfono de contacto principal no válido.": "Teléfono de contacto principal no válido.",
  };
  if (map[message]) return map[message];
  if (message.includes("CUSTOMER_WEBSITE_INVALID")) return "Web no válida.";
  if (message.includes("CUSTOMER_GENERAL_EMAIL_INVALID")) return "Email general no válido.";
  if (message.includes("CUSTOMER_BILLING_EMAIL_INVALID")) return "Email de facturación no válido.";
  if (message.includes("CUSTOMER_PRIMARY_CONTACT_EMAIL_INVALID")) return "Email de contacto principal no válido.";
  if (message.includes("CUSTOMER_GENERAL_PHONE_INVALID")) return "El teléfono general debe tener al menos 6 dígitos.";
  if (message.includes("CUSTOMER_PRIMARY_CONTACT_PHONE_INVALID")) return "Teléfono de contacto principal no válido.";
  if (message.includes("CUSTOMER_TAX_ID_INVALID")) return "El CIF/NIF/NIE no es válido.";
  if (message.includes("Ya existe") && message.includes("documento fiscal")) {
    return "Ya existe una entidad con ese documento fiscal.";
  }
  if (message.toLowerCase().includes("facturación")) return "Email de facturación no válido.";
  if (message.toLowerCase().includes("contacto") && message.toLowerCase().includes("email")) return "Email de contacto principal no válido.";
  if (message.toLowerCase().includes("contacto") && message.toLowerCase().includes("tel")) return "Teléfono de contacto principal no válido.";
  if (message.toLowerCase().includes("general") && message.toLowerCase().includes("email")) return "Email general no válido.";
  if (message.toLowerCase().includes("general") && message.toLowerCase().includes("tel")) return "El teléfono general debe tener al menos 6 dígitos.";
  if (message.toLowerCase().includes("email")) return "Introduce un email válido.";
  if (message.toLowerCase().includes("tel")) return "El teléfono debe tener al menos 6 dígitos.";
  if (message.includes("fiscal") || message.includes("CIF") || message.includes("NIF")) return "El CIF/NIF/NIE no es válido.";
  return "No se pudo guardar el cliente.";
}

function fieldFromError(message: string): keyof CustomerFormState | "fiscalAddressLine1" | "primaryContactDisplayName" | null {
  const lower = message.toLowerCase();
  if (lower.includes("website") || lower.includes("web no válida")) return "website";
  if (lower.includes("facturación") || lower.includes("billingemail")) return "billingEmail";
  if (lower.includes("contacto") && lower.includes("email")) return "primaryContactEmail";
  if (lower.includes("contacto") && lower.includes("tel")) return "primaryContactPhone";
  if (lower.includes("general") && lower.includes("email")) return "generalEmail";
  if (lower.includes("general") && lower.includes("tel")) return "generalPhone";
  if (lower.includes("razón")) return "legalName";
  if (message.includes("documento fiscal")) return "taxId";
  if (message.includes("CIF") || message.includes("NIF")) return "taxId";
  if (message.includes("dirección fiscal")) return "fiscalAddressLine1";
  if (message.includes("responsable")) return "salesOwnerUserId";
  if (message.includes("contacto")) return "primaryContactDisplayName";
  if (lower.includes("email")) return "generalEmail";
  if (lower.includes("tel")) return "generalPhone";
  return null;
}

function fieldFromPath(path: string): keyof CustomerFormState | "fiscalAddressLine1" | "primaryContactDisplayName" | null {
  const normalized = path.replace(/\[\d+\]/g, "").toLowerCase();
  if (normalized === "website") return "website";
  if (normalized === "taxid") return "taxId";
  if (normalized === "generalemail") return "generalEmail";
  if (normalized === "generalphone") return "generalPhone";
  if (normalized === "billingemail") return "billingEmail";
  if (normalized === "salesowneruserid") return "salesOwnerUserId";
  if (normalized === "fiscaladdress.line1") return "fiscalAddressLine1";
  if (normalized === "initialcontact.displayname") return "primaryContactDisplayName";
  if (normalized === "initialcontact.primaryemail") return "primaryContactEmail";
  if (normalized === "initialcontact.primaryphone") return "primaryContactPhone";
  return null;
}

function extractFieldErrors(error: unknown): { formError: string; fieldErrors: Record<string, string> } {
  const candidate = error as any;
  const issues = candidate?.data?.zodError?.fieldErrors ?? candidate?.shape?.data?.zodError?.fieldErrors;
  const zodIssues = candidate?.data?.zodIssues ?? candidate?.shape?.data?.zodIssues;
  const fieldErrors: Record<string, string> = {};
  if (Array.isArray(zodIssues)) {
    for (const issue of zodIssues) {
      const path = Array.isArray(issue?.path) ? issue.path.join(".") : "";
      const field = fieldFromPath(path);
      if (field && typeof issue?.message === "string") fieldErrors[field] = cleanError(issue.message);
    }
  }
  if (issues && typeof issues === "object") {
    for (const [path, messages] of Object.entries(issues)) {
      const field = fieldFromPath(path);
      const message = Array.isArray(messages) ? messages[0] : messages;
      if (field && typeof message === "string" && !fieldErrors[field]) fieldErrors[field] = cleanError(message);
    }
  }
  const rawMessage = typeof candidate?.message === "string" ? candidate.message : "";
  const fallbackField = fieldFromError(rawMessage);
  if (fallbackField && !fieldErrors[fallbackField]) fieldErrors[fallbackField] = cleanError(rawMessage);
  const firstFieldError = Object.values(fieldErrors)[0];
  return {
    formError: firstFieldError ?? "No se pudo guardar el cliente.",
    fieldErrors,
  };
}

interface CustomerFormState {
  entityKind: EntityKind;
  legalName: string;
  firstName: string;
  lastName: string;
  tradeName: string;
  taxId: string;
  taxCountryCode: string;
  website: string;
  generalEmail: string;
  generalPhone: string;
  lifecycleStage: "prospect" | "customer";
  salesOwnerUserId: string;
  customerSince: string;
  billingEmail: string;
  defaultPaymentMethod: "transfer" | "check" | "direct_debit" | "cash" | "other";
  defaultPaymentTermDays: string;
  invoiceNotes: string;
  commercialNotes: string;
  fiscalAddressLine1: string;
  fiscalAddressLine2: string;
  fiscalPostalCode: string;
  fiscalCity: string;
  fiscalState: string;
  billingSameAsFiscal: boolean;
  billingAddressLine1: string;
  billingPostalCode: string;
  billingCity: string;
  primaryContactDisplayName: string;
  primaryContactEmail: string;
  primaryContactPhone: string;
  missingPrimaryContactJustification: string;
  expectedUpdatedAt: string;
}

const EMPTY_FORM: CustomerFormState = {
  entityKind: "company",
  legalName: "",
  firstName: "",
  lastName: "",
  tradeName: "",
  taxId: "",
  taxCountryCode: "ES",
  website: "",
  generalEmail: "",
  generalPhone: "",
  lifecycleStage: "prospect",
  salesOwnerUserId: SALES_OWNER_UNASSIGNED,
  customerSince: "",
  billingEmail: "",
  defaultPaymentMethod: "transfer",
  defaultPaymentTermDays: "30",
  invoiceNotes: "",
  commercialNotes: "",
  fiscalAddressLine1: "",
  fiscalAddressLine2: "",
  fiscalPostalCode: "",
  fiscalCity: "",
  fiscalState: "",
  billingSameAsFiscal: true,
  billingAddressLine1: "",
  billingPostalCode: "",
  billingCity: "",
  primaryContactDisplayName: "",
  primaryContactEmail: "",
  primaryContactPhone: "",
  missingPrimaryContactJustification: "",
  expectedUpdatedAt: "",
};

function OptionalFields({ title, initiallyOpen, children }: { title: string; initiallyOpen: boolean; children: ReactNode }) {
  return (
    <details open={initiallyOpen} className="min-w-0 rounded-md border p-3 md:col-span-2">
      <summary className="cursor-pointer text-sm font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">{title}</summary>
      <div className="mt-3 grid gap-3 md:grid-cols-2">{children}</div>
    </details>
  );
}

function FieldError({ message }: { message?: string }) {
  return message ? <p className="text-xs text-destructive">{message}</p> : null;
}

export default function CustomerForm() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const journeySearch = useSearch();
  const newWork = isNewWorkSearch(journeySearch);
  const directWork = !newWork && isDirectWorkSearch(journeySearch);
  const [, editParams] = useRoute("/customers/:partnerId/edit");
  const isEdit = Boolean(editParams?.partnerId);
  const partnerId = editParams?.partnerId ? Number(editParams.partnerId) : null;
  const [form, setForm] = useState<CustomerFormState>(() => ({
    ...EMPTY_FORM,
    lifecycleStage: !isEdit && (newWork || directWork) ? "customer" : "prospect",
  }));
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const formRef = useRef<HTMLFormElement>(null);
  const submitting = useRef(false);
  const hasContact = Boolean(form.primaryContactDisplayName.trim() || form.primaryContactEmail.trim() || form.primaryContactPhone.trim());
  const utils = trpc.useUtils();

  // Reveal server-side errors even when an optional section was collapsed.
  useEffect(() => {
    if (!Object.keys(fieldErrors).length) return;
    formRef.current?.querySelectorAll<HTMLDetailsElement>("details").forEach(section => {
      if (section.querySelector('[aria-invalid="true"]')) section.open = true;
    });
  }, [fieldErrors]);

  const canAccess = user ? CUSTOMER_ACCESS_ROLES.includes(user.role) : false;
  const permissions = trpc.customers.permissions.useQuery(undefined, { enabled: canAccess });
  const detail = trpc.customers.get360.useQuery({ partnerId: partnerId! }, { enabled: Boolean(isEdit && partnerId && user) });
  const salesOwners = trpc.customers.listAssignableSalesOwners.useQuery(undefined, { enabled: canAccess });
  const normalizedTaxId = normalizedTaxIdForDuplicateCheck(form.taxId);
  const duplicateTaxIdQuery = trpc.customers.findDuplicateTaxId.useQuery({
    taxId: normalizedTaxId ?? "",
    taxCountryCode: form.taxCountryCode.trim().toUpperCase(),
    ...(isEdit && partnerId ? { excludePartnerId: partnerId } : {}),
  }, {
    enabled: Boolean(canAccess && normalizedTaxId && form.taxCountryCode.trim().toUpperCase() === "ES"),
  });

  useEffect(() => {
    if (!detail.data || !isEdit) return;
    const kind = detail.data.entityType === "self_employed" || detail.data.entityType === "individual"
      ? detail.data.entityType as EntityKind
      : (["public_administration", "ute", "association_foundation"].includes(detail.data.entityType)
        ? detail.data.entityType as EntityKind
        : "company");
    const fiscal = detail.data.addresses.find((a) => a.addressType === "fiscal");
    const billing = detail.data.addresses.find((a) => a.addressType === "billing" && a.isActive !== false);
    const primaryContact =
      detail.data.contacts.find((c) => c.role === "primary" && c.isPrimary)
      ?? detail.data.contacts.find((c) => c.isPrimary)
      ?? detail.data.contacts.find((c) => c.role === "primary");

    // businessPartners has no firstName/lastName columns; for persons the
    // combined legalName is split heuristically so legalNameFromParts round-trips
    // to the same value (preservation of the stored legalName).
    const isPerson = partyTypeFor(kind) === "person";
    const storedLegalName = detail.data.legalName ?? "";
    let firstName = "";
    let lastName = "";
    if (isPerson) {
      const trimmed = storedLegalName.trim();
      const spaceIdx = trimmed.indexOf(" ");
      if (spaceIdx >= 0) {
        firstName = trimmed.slice(0, spaceIdx);
        lastName = trimmed.slice(spaceIdx + 1).trim();
      } else {
        firstName = trimmed;
      }
    }

    setForm((current) => ({
      ...current,
      entityKind: kind,
      legalName: isPerson ? (current.legalName || "") : storedLegalName,
      firstName,
      lastName,
      tradeName: detail.data.tradeName ?? "",
      taxId: detail.data.taxId ?? "",
      taxCountryCode: detail.data.taxCountryCode ?? "ES",
      website: detail.data.website ?? "",
      generalEmail: detail.data.generalEmail ?? "",
      generalPhone: detail.data.generalPhone ?? "",
      lifecycleStage: (detail.data.lifecycleStage === "customer" ? "customer" : "prospect"),
      salesOwnerUserId: toSalesOwnerSelect(detail.data.salesOwnerUserId != null ? String(detail.data.salesOwnerUserId) : ""),
      customerSince: detail.data.customerSince ?? "",
      billingEmail: detail.data.billingEmail ?? "",
      defaultPaymentMethod: (detail.data.defaultPaymentMethod as CustomerFormState["defaultPaymentMethod"]) ?? "transfer",
      defaultPaymentTermDays: detail.data.defaultPaymentTermDays != null ? String(detail.data.defaultPaymentTermDays) : "30",
      invoiceNotes: detail.data.invoiceNotes ?? "",
      fiscalAddressLine1: fiscal?.line1 ?? "",
      fiscalAddressLine2: (fiscal?.line2 as string | null | undefined) ?? "",
      fiscalPostalCode: fiscal?.postalCode ?? "",
      fiscalCity: fiscal?.city ?? "",
      fiscalState: (fiscal?.state as string | null | undefined) ?? "",
      billingSameAsFiscal: billing ? false : true,
      billingAddressLine1: billing?.line1 ?? "",
      billingPostalCode: billing?.postalCode ?? "",
      billingCity: billing?.city ?? "",
      primaryContactDisplayName: primaryContact?.displayName ?? "",
      primaryContactEmail: primaryContact?.primaryEmail ?? "",
      primaryContactPhone: primaryContact?.primaryPhone ?? "",
      expectedUpdatedAt: detail.data.updatedAt ?? "",
    }));
  }, [detail.data, isEdit]);

  useEffect(() => {
    if (isEdit || !user || user.role !== "sales") return;
    setForm((current) => {
      if (current.salesOwnerUserId && current.salesOwnerUserId !== SALES_OWNER_UNASSIGNED) return current;
      return { ...current, salesOwnerUserId: String(user.id) };
    });
  }, [isEdit, user]);

  const title = isEdit ? "Editar cliente" : "Nuevo cliente";
  const canWrite = canManageCustomers(user?.role, permissions.data?.canCreateCustomer);
  const missingForCustomer = useMemo(() => {
    if (form.lifecycleStage !== "customer") return [];
    const missing: string[] = [];
    const requiresSpanishTaxId = form.taxCountryCode.trim().toUpperCase() === "ES";
    if (requiresSpanishTaxId && !form.taxId.trim()) missing.push("documento fiscal");
    if (partyTypeFor(form.entityKind) === "organization" && !form.tradeName.trim()) missing.push("nombre comercial");
    if (!form.fiscalAddressLine1.trim()) missing.push("dirección fiscal");
    if (!form.fiscalPostalCode.trim()) missing.push("código postal fiscal");
    if (!form.fiscalCity.trim()) missing.push("municipio fiscal");
    if (!form.fiscalState.trim()) missing.push("provincia fiscal");
    if (!/^[A-Z]{2}$/.test(form.taxCountryCode.trim().toUpperCase())) missing.push("país fiscal");
    if (!form.billingEmail.trim()) missing.push("email de facturación");
    if (!form.salesOwnerUserId.trim() || form.salesOwnerUserId === SALES_OWNER_UNASSIGNED) missing.push("responsable comercial");
    return missing;
  }, [form]);

  const saveCustomer = trpc.customers.create.useMutation({
    onSuccess: (result) => {
      submitting.current = false;
      if ("ok" in result && result.ok === false) {
        setFormError("El alta requiere revisión manual por posible duplicidad.");
        return;
      }
      utils.customers.list.invalidate();
      if ("partnerId" in result) setLocation(newWork ? newWorkCustomerPath(result.partnerId) : directWork ? directWorkCustomerPath(result.partnerId) : `/customers/${result.partnerId}?tab=contactos`);
    },
    onError: (error) => {
      submitting.current = false;
      const result = extractFieldErrors(error);
      setFormError(result.formError);
      setFieldErrors(result.fieldErrors);
    },
  });

  const updateCustomer = trpc.customers.updateProfile.useMutation({
    onSuccess: () => {
      submitting.current = false;
      if (partnerId) {
        utils.customers.get360.invalidate({ partnerId });
        utils.customers.list.invalidate();
        setLocation(`/customers/${partnerId}`);
      }
    },
    onError: (error) => {
      submitting.current = false;
      const result = extractFieldErrors(error);
      setFormError(result.formError);
      setFieldErrors(result.fieldErrors);
    },
  });

  function set<K extends keyof CustomerFormState>(key: K, value: CustomerFormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
    setFieldErrors((current) => {
      const next = { ...current };
      delete next[key as string];
      return next;
    });
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    if (submitting.current) return;
    setFormError(null);
    setFieldErrors({});
    const legalName = legalNameFromParts(form);
    if (!legalName) {
      setFormError("Razón social obligatoria.");
      setFieldErrors({ legalName: "Razón social obligatoria." });
      return;
    }
    if (duplicateTaxIdQuery.data) {
      setFormError(`Ya existe esta entidad: ${duplicateTaxIdQuery.data.displayName} [${duplicateTaxIdQuery.data.code}]. Abre el cliente existente antes de continuar.`);
      return;
    }
    if (duplicateTaxIdQuery.isFetching && normalizedTaxId) {
      setFormError("Comprobando el documento fiscal. Espera a que termine la consulta antes de guardar.");
      return;
    }
    if (missingForCustomer.length > 0) {
      const message = `Para guardar como Cliente faltan: ${missingForCustomer.join(", ")}.`;
      setFormError(message);
      const nextFieldErrors: Record<string, string> = {};
      if (form.taxCountryCode.trim().toUpperCase() === "ES" && !form.taxId.trim()) nextFieldErrors.taxId = "El CIF/NIF/NIE es obligatorio para convertir a Cliente en España.";
      if (partyTypeFor(form.entityKind) === "organization" && !form.tradeName.trim()) nextFieldErrors.tradeName = "El nombre comercial es obligatorio para una organización Cliente.";
      if (!form.fiscalAddressLine1.trim()) nextFieldErrors.fiscalAddressLine1 = "La dirección fiscal es obligatoria para convertir a Cliente.";
      if (!form.fiscalPostalCode.trim()) nextFieldErrors.fiscalPostalCode = "El código postal fiscal es obligatorio para convertir a Cliente.";
      if (!form.fiscalCity.trim()) nextFieldErrors.fiscalCity = "El municipio fiscal es obligatorio para convertir a Cliente.";
      if (!form.fiscalState.trim()) nextFieldErrors.fiscalState = "La provincia fiscal es obligatoria para convertir a Cliente.";
      if (!/^[A-Z]{2}$/.test(form.taxCountryCode.trim().toUpperCase())) nextFieldErrors.taxCountryCode = "El país fiscal debe ser un código de dos letras.";
      if (!form.billingEmail.trim()) nextFieldErrors.billingEmail = "El email de facturación es obligatorio para convertir a Cliente.";
      if (!form.salesOwnerUserId.trim() || form.salesOwnerUserId === SALES_OWNER_UNASSIGNED) {
        nextFieldErrors.salesOwnerUserId = "El responsable comercial es obligatorio para convertir a Cliente.";
      }
      setFieldErrors(nextFieldErrors);
      return;
    }
    if (!isEdit && hasContact) {
      const errors: Record<string, string> = {};
      if (!form.primaryContactDisplayName.trim()) errors.primaryContactDisplayName = "Indica el nombre del contacto.";
      if (form.primaryContactEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.primaryContactEmail.trim())) errors.primaryContactEmail = "Email de contacto principal no válido.";
      if (form.primaryContactPhone.trim() && form.primaryContactPhone.replace(/\D/g, "").length < 6) errors.primaryContactPhone = "Teléfono de contacto principal no válido.";
      if (Object.keys(errors).length) {
        setFieldErrors(errors);
        setFormError("Revisa los datos del contacto principal.");
        return;
      }
    }
    const partyType = partyTypeFor(form.entityKind);
    const fiscalAddress = form.fiscalAddressLine1.trim()
      ? {
          addressType: "fiscal" as const,
          line1: form.fiscalAddressLine1,
          line2: form.fiscalAddressLine2 || null,
          postalCode: form.fiscalPostalCode || null,
          city: form.fiscalCity || null,
          state: form.fiscalState || null,
          countryCode: form.taxCountryCode || "ES",
          isPrimary: true,
        }
      : null;
    const billingAddress = !form.billingSameAsFiscal && form.billingAddressLine1.trim()
      ? {
          addressType: "billing" as const,
          line1: form.billingAddressLine1,
          postalCode: form.billingPostalCode || null,
          city: form.billingCity || null,
          countryCode: form.taxCountryCode || "ES",
          isPrimary: true,
        }
      : null;

    const payload = {
      partyType,
      entityType: form.entityKind,
      legalName,
      firstName: form.firstName || null,
      lastName: form.lastName || null,
      tradeName: form.tradeName || null,
      taxId: form.taxId || null,
      taxCountryCode: form.taxCountryCode || "ES",
      website: form.website || null,
      generalEmail: form.generalEmail || null,
      generalPhone: form.generalPhone || null,
      lifecycleStage: form.lifecycleStage,
      salesOwnerUserId: fromSalesOwnerSelect(form.salesOwnerUserId),
      customerSince: form.customerSince || null,
      billingEmail: form.billingEmail || null,
      defaultPaymentMethod: form.defaultPaymentMethod,
      defaultPaymentTermDays: form.defaultPaymentTermDays ? Number(form.defaultPaymentTermDays) : null,
      invoiceNotes: form.invoiceNotes || null,
      commercialNotes: form.commercialNotes || null,
      fiscalAddress,
      billingAddress,
    };

    submitting.current = true;
    if (isEdit && partnerId) {
      updateCustomer.mutate({
        partnerId,
        partyType: payload.partyType,
        entityType: payload.entityType,
        legalName: payload.legalName,
        tradeName: payload.tradeName,
        taxId: payload.taxId,
        taxCountryCode: payload.taxCountryCode,
        website: payload.website,
        generalEmail: payload.generalEmail,
        generalPhone: payload.generalPhone,
        lifecycleStage: payload.lifecycleStage,
        salesOwnerUserId: payload.salesOwnerUserId,
        customerSince: payload.customerSince,
        billingEmail: payload.billingEmail,
        defaultPaymentMethod: payload.defaultPaymentMethod,
        defaultPaymentTermDays: payload.defaultPaymentTermDays,
        invoiceNotes: payload.invoiceNotes,
        fiscalAddress: payload.fiscalAddress,
        billingAddress: payload.billingAddress,
        expectedUpdatedAt: form.expectedUpdatedAt || null,
        taxIdChangeReason: form.taxId.trim() ? "Actualización desde Customer Master Data" : null,
      });
    } else {
      saveCustomer.mutate({
        ...payload,
        initialContact: hasContact ? {
          displayName: form.primaryContactDisplayName.trim(),
          primaryEmail: form.primaryContactEmail.trim() || null,
          primaryPhone: form.primaryContactPhone.trim() || null,
        } : null,
        missingPrimaryContactJustification: hasContact ? null : form.missingPrimaryContactJustification.trim() || null,
      });
    }
  }

  if (!user) return null;

  if (!canAccess || !canWrite) {
    return (
      <DashboardLayout>
        <div className="flex min-h-[400px] flex-col items-center justify-center gap-3 text-muted-foreground">
          <AlertCircle className="h-8 w-8" />
          <p className="text-sm">No tienes permiso para gestionar clientes.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <form ref={formRef} onSubmit={submit} className="space-y-4 p-4 sm:p-6" onInvalidCapture={(event) => {
        const section = (event.target as HTMLElement).closest("details");
        if (section) section.open = true;
      }}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => setLocation(isEdit && partnerId ? `/customers/${partnerId}` : newWork ? "/customers?newWork=1" : directWork ? "/customers?directWork=1" : "/customers")}>
              <ArrowLeft className="mr-1 h-4 w-4" />
              Clientes
            </Button>
            <h1 className="text-xl font-semibold">{title}</h1>
          </div>
          <Button type="submit" disabled={saveCustomer.isPending || updateCustomer.isPending}>
            <Save className="mr-1 h-4 w-4" />
            {saveCustomer.isPending || updateCustomer.isPending ? "Guardando" : "Guardar"}
          </Button>
        </div>

        {formError && <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">{formError}</div>}
        {duplicateTaxIdQuery.data && (
          <div role="status" className="flex flex-wrap items-center gap-1 rounded-md border border-amber-500/40 bg-amber-50 p-3 text-sm text-amber-950">
            <span>Ya existe esta entidad: <strong>{duplicateTaxIdQuery.data.displayName}</strong> [{duplicateTaxIdQuery.data.code}].</span>
            <button type="button" className="underline underline-offset-2" onClick={() => setLocation(`/customers/${duplicateTaxIdQuery.data!.partnerId}`)}>
              Abrir cliente existente.
            </button>
          </div>
        )}

        <p className="text-sm text-muted-foreground">
          {newWork ? "Crea el cliente para continuar con el contrato de la nueva obra. " : directWork ? "Crea el cliente para continuar con el trabajo. " : "Introduce la identidad y elige Cliente o Prospecto según corresponda. "}
          Los campos con * son obligatorios. Puedes ampliar los datos opcionales cuando los necesites.
        </p>
        <Card>
          <CardHeader><CardTitle className="text-sm">Identidad</CardTitle></CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-2">
            <div className="min-w-0 space-y-1.5">
              <Label htmlFor="customer-entityKind">Tipo de entidad *</Label>
              <Select value={form.entityKind} onValueChange={(v: EntityKind) => set("entityKind", v)}>
                <SelectTrigger className="w-full min-w-0 [&_[data-slot=select-value]]:truncate" id="customer-entityKind"><SelectValue /></SelectTrigger>
                <SelectContent className="max-w-[calc(100vw-2rem)]">{ENTITY_KIND_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="min-w-0 space-y-1.5">
              <Label htmlFor="customer-lifecycleStage">Estado *</Label>
              <Select value={form.lifecycleStage} onValueChange={(v: "prospect" | "customer") => set("lifecycleStage", v)}>
                <SelectTrigger className="w-full min-w-0 [&_[data-slot=select-value]]:truncate" id="customer-lifecycleStage"><SelectValue /></SelectTrigger>
                <SelectContent className="max-w-[calc(100vw-2rem)]">
                  <SelectItem value="prospect">Prospecto</SelectItem>
                  <SelectItem value="customer">Cliente</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {partyTypeFor(form.entityKind) === "person" ? (
              <>
                <div className="min-w-0 space-y-1.5">
                  <Label htmlFor="customer-firstName">Nombre *</Label>
                  <Input id="customer-firstName" value={form.firstName} onChange={(e) => set("firstName", e.target.value)} aria-invalid={Boolean(fieldErrors.legalName)} />
                </div>
                <div className="min-w-0 space-y-1.5">
                  <Label htmlFor="customer-lastName">Apellidos *</Label>
                  <Input id="customer-lastName" value={form.lastName} onChange={(e) => set("lastName", e.target.value)} aria-invalid={Boolean(fieldErrors.legalName)} />
                </div>
              </>
            ) : (
              <div className="min-w-0 space-y-1.5 md:col-span-2">
                <Label htmlFor="customer-legalName">Razón social *</Label>
                <Input id="customer-legalName" value={form.legalName} onChange={(e) => set("legalName", e.target.value)} aria-invalid={Boolean(fieldErrors.legalName)} />
                <FieldError message={fieldErrors.legalName} />
              </div>
            )}

            <div className="min-w-0 space-y-1.5">
              <Label htmlFor="customer-taxId">NIF/NIE/CIF {form.lifecycleStage === "customer" && form.taxCountryCode.trim().toUpperCase() === "ES" ? "*" : ""}</Label>
              <Input id="customer-taxId" value={form.taxId} onChange={(e) => set("taxId", e.target.value)} required={form.lifecycleStage === "customer" && form.taxCountryCode.trim().toUpperCase() === "ES"} aria-invalid={Boolean(fieldErrors.taxId)} />
              <FieldError message={fieldErrors.taxId} />
            </div>
            <div className="min-w-0 space-y-1.5">
              <Label htmlFor="customer-taxCountryCode">País fiscal {form.lifecycleStage === "customer" ? "*" : ""}</Label>
              <Input id="customer-taxCountryCode" value={form.taxCountryCode} onChange={(e) => set("taxCountryCode", e.target.value.toUpperCase())} maxLength={2} required={form.lifecycleStage === "customer"} aria-invalid={Boolean(fieldErrors.taxCountryCode)} />
              <FieldError message={fieldErrors.taxCountryCode} />
            </div>

              <div className="min-w-0 space-y-1.5">
                <Label htmlFor="customer-tradeName">Nombre comercial {form.lifecycleStage === "customer" && partyTypeFor(form.entityKind) === "organization" ? "*" : ""}</Label>
                <Input id="customer-tradeName" value={form.tradeName} onChange={(e) => set("tradeName", e.target.value)} aria-invalid={Boolean(fieldErrors.tradeName)} required={form.lifecycleStage === "customer" && partyTypeFor(form.entityKind) === "organization"} />
                <FieldError message={fieldErrors.tradeName} />
              </div>
            <OptionalFields title="Más datos de identidad y contacto general" initiallyOpen={isEdit}>
              <div className="min-w-0 space-y-1.5">
                <Label htmlFor="customer-website">Web</Label>
                <Input id="customer-website" value={form.website} onChange={(e) => set("website", e.target.value)} aria-invalid={Boolean(fieldErrors.website)} />
                <FieldError message={fieldErrors.website} />
              </div>
              <div className="min-w-0 space-y-1.5">
                <Label htmlFor="customer-generalEmail">Email general</Label>
                <Input id="customer-generalEmail" value={form.generalEmail} onChange={(e) => set("generalEmail", e.target.value)} aria-invalid={Boolean(fieldErrors.generalEmail)} />
                <FieldError message={fieldErrors.generalEmail} />
              </div>
              <div className="min-w-0 space-y-1.5">
                <Label htmlFor="customer-generalPhone">Teléfono general</Label>
                <Input id="customer-generalPhone" value={form.generalPhone} onChange={(e) => set("generalPhone", e.target.value)} aria-invalid={Boolean(fieldErrors.generalPhone)} />
                <FieldError message={fieldErrors.generalPhone} />
              </div>
            </OptionalFields>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-sm">Dirección fiscal</CardTitle></CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-2">
            <div className="min-w-0 space-y-1.5 md:col-span-2">
              <Label htmlFor="customer-fiscalAddressLine1">Dirección {form.lifecycleStage === "customer" ? "*" : ""}</Label>
              <Input id="customer-fiscalAddressLine1" value={form.fiscalAddressLine1} onChange={(e) => set("fiscalAddressLine1", e.target.value)} required={form.lifecycleStage === "customer"} aria-invalid={Boolean(fieldErrors.fiscalAddressLine1)} />
              <FieldError message={fieldErrors.fiscalAddressLine1} />
            </div>
            <div className="min-w-0 space-y-1.5">
              <Label htmlFor="customer-fiscalPostalCode">Código postal {form.lifecycleStage === "customer" ? "*" : ""}</Label>
              <Input id="customer-fiscalPostalCode" value={form.fiscalPostalCode} onChange={(e) => set("fiscalPostalCode", e.target.value)} required={form.lifecycleStage === "customer"} aria-invalid={Boolean(fieldErrors.fiscalPostalCode)} />
              <FieldError message={fieldErrors.fiscalPostalCode} />
            </div>
            <div className="min-w-0 space-y-1.5">
              <Label htmlFor="customer-fiscalCity">Municipio {form.lifecycleStage === "customer" ? "*" : ""}</Label>
              <Input id="customer-fiscalCity" value={form.fiscalCity} onChange={(e) => set("fiscalCity", e.target.value)} required={form.lifecycleStage === "customer"} aria-invalid={Boolean(fieldErrors.fiscalCity)} />
              <FieldError message={fieldErrors.fiscalCity} />
            </div>
            <div className="min-w-0 space-y-1.5">
              <Label htmlFor="customer-fiscalState">Provincia {form.lifecycleStage === "customer" ? "*" : ""}</Label>
              <Input id="customer-fiscalState" value={form.fiscalState} onChange={(e) => set("fiscalState", e.target.value)} required={form.lifecycleStage === "customer"} aria-invalid={Boolean(fieldErrors.fiscalState)} />
              <FieldError message={fieldErrors.fiscalState} />
            </div>
            <OptionalFields title="Dirección fiscal adicional" initiallyOpen={isEdit}>
              <div className="min-w-0 space-y-1.5 md:col-span-2">
                <Label htmlFor="customer-fiscalAddressLine2">Línea adicional</Label>
                <Input id="customer-fiscalAddressLine2" value={form.fiscalAddressLine2} onChange={(e) => set("fiscalAddressLine2", e.target.value)} />
              </div>
            </OptionalFields>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-sm">Facturación y comercial</CardTitle></CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-2">
            <div className="min-w-0 space-y-1.5">
              <Label htmlFor="customer-salesOwnerUserId">Responsable comercial {form.lifecycleStage === "customer" ? "*" : ""}</Label>
              <Select
                value={toSalesOwnerSelect(form.salesOwnerUserId)}
                onValueChange={(v) => set("salesOwnerUserId", v === SALES_OWNER_UNASSIGNED ? SALES_OWNER_UNASSIGNED : v)}
                disabled={!permissions.data?.canCreateCustomer}
              >
                <SelectTrigger className="w-full min-w-0 [&_[data-slot=select-value]]:truncate" id="customer-salesOwnerUserId" aria-invalid={Boolean(fieldErrors.salesOwnerUserId)}>
                  <SelectValue placeholder="Sin asignar" />
                </SelectTrigger>
                <SelectContent className="max-w-[calc(100vw-2rem)]">
                  <SelectItem value={SALES_OWNER_UNASSIGNED}>Sin asignar</SelectItem>
                  {(salesOwners.data ?? []).map(u => (
                    <SelectItem key={u.id} value={String(u.id)}>
                      {u.name || u.email} · {u.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldError message={fieldErrors.salesOwnerUserId} />
            </div>

            <div className="min-w-0 space-y-1.5 md:col-span-2">
              <Label htmlFor="customer-billingEmail">Email de facturación {form.lifecycleStage === "customer" ? "*" : ""}</Label>
              <Input id="customer-billingEmail" value={form.billingEmail} onChange={(e) => set("billingEmail", e.target.value)} required={form.lifecycleStage === "customer"} aria-invalid={Boolean(fieldErrors.billingEmail)} />
              <FieldError message={fieldErrors.billingEmail} />
            </div>

            <OptionalFields title="Condiciones de pago y notas opcionales" initiallyOpen={isEdit}>
              <div className="min-w-0 space-y-1.5">
                <Label htmlFor="customer-customerSince">Cliente desde</Label>
                <Input id="customer-customerSince" type="date" value={form.customerSince} onChange={(e) => set("customerSince", e.target.value)} />
              </div>
              <div className="min-w-0 space-y-1.5">
                <Label htmlFor="customer-defaultPaymentMethod">Método de pago</Label>
                <Select value={form.defaultPaymentMethod} onValueChange={(v: CustomerFormState["defaultPaymentMethod"]) => set("defaultPaymentMethod", v)}>
                  <SelectTrigger className="w-full min-w-0 [&_[data-slot=select-value]]:truncate" id="customer-defaultPaymentMethod"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-w-[calc(100vw-2rem)]">{PAYMENT_METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="min-w-0 space-y-1.5">
                <Label htmlFor="customer-defaultPaymentTermDays">Plazo de pago</Label>
                <Input id="customer-defaultPaymentTermDays" type="number" min={0} max={365} value={form.defaultPaymentTermDays} onChange={(e) => set("defaultPaymentTermDays", e.target.value)} />
              </div>
              <div className="min-w-0 space-y-1.5 md:col-span-2">
                <Label htmlFor="customer-invoiceNotes">Notas de facturación</Label>
                <Textarea id="customer-invoiceNotes" maxLength={2000} value={form.invoiceNotes} onChange={(e) => set("invoiceNotes", e.target.value)} />
              </div>
              {!isEdit && (
                <div className="min-w-0 space-y-1.5 md:col-span-2">
                  <Label htmlFor="customer-commercialNotes">Notas comerciales del alta</Label>
                  <p className="text-xs text-muted-foreground">Se guardan en el historial del alta.</p>
                  <Textarea id="customer-commercialNotes" maxLength={2000} value={form.commercialNotes} onChange={(e) => set("commercialNotes", e.target.value)} />
                </div>
              )}
            </OptionalFields>
          </CardContent>
        </Card>

        {!isEdit && (
          <Card>
            <CardHeader><CardTitle className="text-sm">Contacto principal</CardTitle></CardHeader>
            <CardContent className="grid gap-3 md:grid-cols-2">
              <p className="text-sm text-muted-foreground md:col-span-2">Opcional. Si añades una persona, indica su nombre; el email y el teléfono pueden completarse después.</p>
              <div className="min-w-0 space-y-1.5">
                <Label htmlFor="customer-primaryContactDisplayName">Nombre del contacto {hasContact ? "*" : ""}</Label>
                <Input id="customer-primaryContactDisplayName" maxLength={255} value={form.primaryContactDisplayName} onChange={(e) => set("primaryContactDisplayName", e.target.value)} aria-invalid={Boolean(fieldErrors.primaryContactDisplayName)} />
                <FieldError message={fieldErrors.primaryContactDisplayName} />
              </div>
              <div className="min-w-0 space-y-1.5"><Label htmlFor="customer-primaryContactEmail">Email</Label><Input id="customer-primaryContactEmail" maxLength={320} value={form.primaryContactEmail} onChange={(e) => set("primaryContactEmail", e.target.value)} aria-invalid={Boolean(fieldErrors.primaryContactEmail)} /><FieldError message={fieldErrors.primaryContactEmail} /></div>
              <div className="min-w-0 space-y-1.5"><Label htmlFor="customer-primaryContactPhone">Teléfono</Label><Input id="customer-primaryContactPhone" maxLength={50} value={form.primaryContactPhone} onChange={(e) => set("primaryContactPhone", e.target.value)} aria-invalid={Boolean(fieldErrors.primaryContactPhone)} /><FieldError message={fieldErrors.primaryContactPhone} /></div>
              {!hasContact && (
                <div className="min-w-0 space-y-1.5 md:col-span-2">
                  <Label htmlFor="missing-contact-reason">Motivo para continuar sin contacto (opcional)</Label>
                  <p id="missing-contact-help" className="text-sm text-muted-foreground">Si aún no conoces a la persona de contacto, puedes indicarlo aquí y añadirla más adelante desde la ficha del cliente. El motivo se guarda en el historial del alta.</p>
                  <Textarea id="missing-contact-reason" aria-describedby="missing-contact-help" maxLength={2000} value={form.missingPrimaryContactJustification} onChange={(e) => set("missingPrimaryContactJustification", e.target.value)} />
                </div>
              )}
            </CardContent>
          </Card>
        )}
        <div className="flex justify-end border-t pt-4">
          <Button type="submit" disabled={saveCustomer.isPending || updateCustomer.isPending}>
            <Save className="mr-1 h-4 w-4" />
            {saveCustomer.isPending || updateCustomer.isPending ? "Guardando" : "Guardar"}
          </Button>
        </div>
      </form>
    </DashboardLayout>
  );
}
